/**
 * <color-swatches> — colorway picker on a shop card.
 *
 * Swaps the card's preview image to the chosen colorway and re-points the
 * card link and its CTA at that colorway's variant, so the choice survives
 * the click through to the product page instead of being thrown away.
 *
 * Lives on cards rendered by snippets/product-card.liquid, which appear in
 * four sections (home grid, collection, related, search) — hence a
 * self-registering element rather than section-scoped script.
 *
 * Markup contract (see snippets/product-card.liquid):
 *   .pcard                         the card root
 *     [data-card-link]             anchor wrapping media + copy
 *     img.pcard__img               primary <img>
 *     img.pcard__alt               optional hover <img>
 *     <color-swatches role=radiogroup>
 *       button[role=radio][data-color][data-image][data-image-hover][data-url]
 *     [data-card-cta]              optional footer link
 */
if (!customElements.get('color-swatches')) {
  class ColorSwatches extends HTMLElement {
    connectedCallback() {
      this.card = this.closest('.pcard');
      this.swatches = Array.from(this.querySelectorAll('[role="radio"]'));
      if (!this.card || !this.swatches.length) return;

      this.image = this.card.querySelector('.pcard__img');
      this.hoverImage = this.card.querySelector('.pcard__alt');
      this.link = this.card.querySelector('[data-card-link]');
      this.cta = this.card.querySelector('[data-card-cta]');
      this.range = this.card.querySelector('[data-card-range]');

      this.addEventListener('click', this.#onClick);
      this.addEventListener('keydown', this.#onKeydown);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.#onClick);
      this.removeEventListener('keydown', this.#onKeydown);
    }

    #onClick = (event) => {
      const swatch = event.target.closest('[role="radio"]');
      if (!swatch || !this.contains(swatch)) return;
      // The swatch sits outside the card link, but the card itself is
      // clickable in some layouts — never let a colour pick navigate.
      event.preventDefault();
      this.#select(swatch);
    };

    #onKeydown = (event) => {
      const KEYS = {
        ArrowRight: 1,
        ArrowDown: 1,
        ArrowLeft: -1,
        ArrowUp: -1,
      };
      // Let modifier chords (browser tab/history shortcuts) through.
      if (event.altKey || event.ctrlKey || event.metaKey) return;

      if (event.key === ' ' || event.key === 'Enter') {
        const swatch = event.target.closest('[role="radio"]');
        if (!swatch) return;
        event.preventDefault();
        this.#select(swatch);
        return;
      }

      const step = KEYS[event.key];
      if (!step) return;
      const current = this.swatches.indexOf(event.target.closest('[role="radio"]'));
      if (current === -1) return;
      event.preventDefault();
      const next = (current + step + this.swatches.length) % this.swatches.length;
      this.#select(this.swatches[next]);
      this.swatches[next].focus();
    };

    /** Rewrite a Shopify CDN image URL's width parameter, preserving ?v=. */
    #atWidth(url, width) {
      return url.includes('width=')
        ? url.replace(/width=\d+/, `width=${width}`)
        : `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
    }

    #srcsetFor(url) {
      return [360, 540, 720, 900, 1080].map((w) => `${this.#atWidth(url, w)} ${w}w`).join(', ');
    }

    #paint(img, url) {
      if (!img) return;
      if (!url) {
        // No photo for this colorway: leave what is on screen rather than
        // blanking the card. Matches the PDP's never-empty gallery rule.
        return;
      }
      img.setAttribute('src', this.#atWidth(url, 900));
      img.setAttribute('srcset', this.#srcsetFor(url));
    }

    #select(swatch) {
      for (const other of this.swatches) {
        const isActive = other === swatch;
        other.setAttribute('aria-checked', isActive ? 'true' : 'false');
        other.tabIndex = isActive ? 0 : -1;
      }

      this.#paint(this.image, swatch.dataset.image);

      // Hover reveal: a second view of the colour just picked. When that
      // colour only has one photo, hide the reveal rather than leave a
      // different colour behind it.
      if (this.hoverImage) {
        if (swatch.dataset.imageHover) {
          this.#paint(this.hoverImage, swatch.dataset.imageHover);
          this.hoverImage.hidden = false;
        } else {
          this.hoverImage.hidden = true;
        }
      }

      // Sizes are per-colourway, not per-product: the hoodie runs S–5XL
      // overall but Sand exists only in 3XL. Show what this colour is
      // actually offered in.
      if (this.range && swatch.dataset.sizes) {
        this.range.textContent = swatch.dataset.sizes;
      }

      const url = swatch.dataset.url;
      if (url) {
        if (this.link) this.link.setAttribute('href', url);
        if (this.cta) this.cta.setAttribute('href', url);
      }
    }
  }

  customElements.define('color-swatches', ColorSwatches);
}
